import * as versionActions from "./ducks/app/version";
import { serializeError } from "serialize-error";
import ReactDOM from "react-dom/client";
import "./index.scss";
import App from "./App";
import { Settings as LuxonSettings } from "luxon";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { reportErrorToBackend } from "./ducks/api";
import { store } from "./redux-store";
import { loadApiKeys } from "./ducks/app/api-keys";
// import { useLocation } from "react-router-dom";
import { isMobile, appVersion } from "./helpers/mobile";
import { isNetworkError } from "./helpers/networkError";
import { registerStaleChunkReload } from "./helpers/staleChunkReload";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { Preferences } from "@capacitor/preferences";

// import {
//   createRoutesFromChildren,
//   matchRoutes,
//   useNavigationType,
// } from "react-router";

LuxonSettings.throwOnInvalid = true;
declare module "luxon" {
  interface TSSettings {
    throwOnInvalid: true;
  }
}

// Connectivity loss is an expected condition on mobile — surfaced to the user
// via UI, not Sentry. Drop these events at the SDK boundary.
const dropNetworkErrors = (event: any, hint?: { originalException?: unknown }) => {
  if (hint?.originalException && isNetworkError(hint.originalException)) {
    return null;
  }
  return event;
};

// Skip local dev servers: without an explicit environment their errors land in
// Sentry tagged "production" (e.g. browser-only Firebase errors on localhost).
if (isMobile && !import.meta.env.DEV) {
  Sentry.init(
    {
      dsn: "https://0fbc4a701d3fcccc19a0d152daea2d3e@o4507542742368256.ingest.de.sentry.io/4508966066978896",
      // Set your release version, such as "getsentry@1.0.0"
      release: `dora-mobile-app@${appVersion}`,
      sendDefaultPii: true,
      // Set your dist version, such as "1"
      dist: appVersion,
      beforeSend: dropNetworkErrors,
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration(),
      ],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for tracing.
      // We recommend adjusting this value in production
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: 0.6,
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        "localhost",
        /^https:\/\/staging\.doranordic\.dk/,
        /^https:\/\/app\.doranordic\.dk/,
      ],
      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // iOS only. The default 2s threshold floods Sentry with hangs from the
      // Scanbot SDK encoding scan JPEGs on the main thread, which we can't fix.
      appHangTimeoutInterval: 7,
    },
    // Forward the init method from @sentry/react
    SentryReact.init
  );
} else {
  SentryReact.init({
    dsn: "https://ade179d0fa07ad6b5c3fbb92337cde8f@o4507542742368256.ingest.de.sentry.io/4507542785425488",
    beforeSend: dropNetworkErrors,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/app.nordic\.dk/],
    integrations: [
      // Sentry.reactRouterV6BrowserTracingIntegration({
      //   useEffect,
      //   useLocation,
      //   useNavigationType,
      //   createRoutesFromChildren,
      //   matchRoutes,
      // }),
      SentryReact.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.6, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: process.env.NODE_ENV !== "development",
  });
}

// The native app ships its chunks inside the bundle, so they can't go stale
// across a server deploy — only the web driver app needs this.
if (!isMobile) {
  registerStaleChunkReload();
}

async function boot() {
  // Apply pending update BEFORE rendering — prevents black/white flash
  if (isMobile) {
    try {
      const { value: bundleJson } = await Preferences.get({
        key: "pending_bundle",
      });
      if (bundleJson) {
        const bundle = JSON.parse(bundleJson);
        console.log("Applying pending update before render:", bundle.version);
        await Preferences.remove({ key: "pending_bundle" });
        await CapacitorUpdater.set(bundle);
        return; // App is reloading, don't render
      }
    } catch (e) {
      console.error("Error applying pending update:", e);
      await Preferences.remove({ key: "pending_bundle" }).catch(() => {});
    }
  }

  // Reset background to white now that we're past the update check
  document.body.style.backgroundColor = "#ffffff";
  document.getElementById("root")!.style.backgroundColor = "#ffffff";

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );
  root.render(<App />);
}

boot();

export const formatError = (err: any) =>
  err instanceof Error
    ? `${err.toString()}\n${err.message}\n${err.stack}`
    : JSON.stringify(err);

window.addEventListener("error", (event) => {
  // Benign browser notice that resize notifications were deferred a frame;
  // fired as an error event with no Error object, nothing actually failed.
  if (event.message?.startsWith("ResizeObserver loop")) return;
  const err = event.error || event;
  if (isNetworkError(err)) return;
  try {
    // Disabled for now, let's see if crossorigin helps
    // if (err?.message === "Script error.") {
    //   reportWarningToBackend({
    //     msg: "uncaught script error",
    //     err: serializeError(err),
    //   });
    // } else {
    reportErrorToBackend({
      msg: "uncaught error",
      err: serializeError(err),
    });
    // }
  } catch {
    // Ignore, don't want our unhandled error handler cause an unhandled error
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (isNetworkError(event.reason)) {
    event.preventDefault();
  }
});

store.dispatch(versionActions.initialize((window as any).env.uiVersion));
store.dispatch(loadApiKeys());

